import * as React from 'react';
import {useDispatch} from 'react-redux';

import {convertToArray, safelyGetFilterByKey} from 'nutshell-core/filter';

import {DashboardCard} from 'shells/dashboard/card/dashboard-card';
import {List} from 'shells/dashboard/list/list';
import {Illustration} from 'shells/illustrations/illustration';
import {ChangeRow} from 'shells/metrics/change-row';
import {Metric} from 'shells/metrics/metric';
import {PromoStateOverlay} from 'shells/promo-state-overlay/promo-state-overlay';

import {LandingPagesReportChart} from '../../../landing-pages/report/landing-pages-report-chart';
import * as actions from '../../../landing-pages/report/pages-actions';
import type {ReportURLParams} from '../../../reports/report-types';

import {DEFAULT_TIMESPAN} from '../dashboard';
import {useGetLandingPagesDashboard} from '../graphql/hooks';
import {mapViewSubmissionItemToListItem} from '../helpers';
import {ViewAllLink} from '../view-all-link';

import styles from './landing-pages-card.module.css';

type Props = {
    onParamChange: (arg1: any, arg2: boolean) => any;
    urlParams: ReportURLParams<'contacts'>;
};

export function LandingPagesCard(props: Props) {
    const dispatch = useDispatch();
    const activeFilters = convertToArray(props.urlParams.filter);
    const dateRangeFilter = safelyGetFilterByKey(activeFilters, 'period');

    const {landingPagesDashboard, isLoading} = useGetLandingPagesDashboard({
        timespan: dateRangeFilter || DEFAULT_TIMESPAN,
    });

    const stat = landingPagesDashboard ? landingPagesDashboard.stat : undefined;
    const numLandingPages = landingPagesDashboard
        ? landingPagesDashboard.numLandingPages
        : undefined;
    const topPages = landingPagesDashboard ? landingPagesDashboard.landingPages : [];
    const listItems = topPages.map((data) =>
        mapViewSubmissionItemToListItem({...data.page, views: data.views})
    );

    // hardcoded since we're not using the url to feed this query
    const reportParams = {
        dateRangeType: 'period',
        facet: 'count',
        gap: 'd',
        segment: 'interactionType',
    } as const;

    React.useEffect(() => {
        dispatch(
            actions.requestLandingPagesReportData({
                reportParams,
                filters: activeFilters,
                // @ts-expect-error
                formType: 1,
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilters]);

    return (
        <DashboardCard
            title='Landing pages'
            subtitle='Design & build landing pages that drive conversions'
            link={<ViewAllLink totalCount={numLandingPages} type='LANDING_PAGES' />}
            isLoading={isLoading}
            mainContent={{
                content: (
                    <div className='flex flex-dir-col full-width full-height gap-8 pt-16 pos-relative'>
                        <div className={styles.header}>
                            <Metric
                                title='Total views from all landing pages'
                                value={stat ? stat.formattedValue : '0'}
                                bottomRow={
                                    stat &&
                                    stat.delta && (
                                        <ChangeRow
                                            percentChange={stat.delta.percentChange}
                                            timespanSuffix={stat.delta.timespanSuffix || ''}
                                            changeType={stat.delta.direction}
                                        />
                                    )
                                }
                                isCentered={true}
                                // @ts-expect-error
                                isLarge={true}
                            />
                        </div>
                        <div className={styles.chart}>
                            <LandingPagesReportChart
                                activeFilters={activeFilters}
                                height={270}
                                containerHeight={270}
                                onParamChange={props.onParamChange}
                                reportParams={reportParams}
                                hasBorder={false}
                                hasGap={false}
                            />
                        </div>
                    </div>
                ),
            }}
            sideContentProps={{
                content: (
                    <List
                        title='Top landing pages'
                        subtitle='Your most viewed landing pages'
                        listItems={listItems}
                        emptyStateText='No views have been recorded'
                    />
                ),
            }}
            promoStateOverlay={
                landingPagesDashboard && Number(landingPagesDashboard.numLandingPages) === 0 ? (
                    <PromoStateOverlay
                        promoStateProps={{
                            isHorizontal: true,
                            imageComponent: (
                                <Illustration src='trial-onboarding/marketing-landing-pages' />
                            ),
                            title: 'Begin creating engaging pages',
                            subtitle:
                                'Explore our comprehensive features for designing landing pages that deliver results.',
                            primaryCta: {
                                text: 'Learn more',
                                url: 'https://support.nutshell.com/en/articles/8790094-landing-pages-create-effective-landing-pages',
                                isExternalLink: true,
                            },
                            secondaryCta: {
                                text: 'Create a page',
                                url: '/email-marketing/landing-pages',
                            },
                            blueImageBackground: true,
                        }}
                    />
                ) : null
            }
        />
    );
}
